import { useState, type ReactNode } from "react";

import type { DashboardMetric, SocialReportDashboard } from "@/shared/api/api-client";
import { EmptyState } from "@/shared/components/charts/EmptyState";
import { Panel } from "@/shared/components/charts/Panel";
import { Select } from "@/shared/components/Select";

import { RANKING_METRICS, ranked, rankingText, sliceLabel, type LabelKind, type RankingMetric, type SourceVisibility } from "./dashboard-v1";
import { NOT_IN_THIS_PHOTO } from "./OverviewTab";

function RankingCard({ title, column, items, kind = "default", showsCena, unavailable }: {
  title: string;
  column: string;
  items: DashboardMetric[];
  kind?: LabelKind;
  showsCena: boolean;
  unavailable?: string;
}) {
  const metrics = RANKING_METRICS.filter(option => showsCena || option.value !== "cena");
  const [metric, setMetric] = useState<RankingMetric>("count");
  const rows = ranked(items, metric);
  return (
    <Panel
      title={title}
      className="dashboard-span-4 ranking-card"
      downloadable={!unavailable}
      action={unavailable ? undefined : (
        <Select name={`${title}-metrica`} aria-label={`Métrica de ${title}`} value={metric} onChange={event => setMetric(event.target.value as RankingMetric)}>
          {metrics.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
        </Select>
      )}
    >
      {unavailable ? <EmptyState label={unavailable}/> : !rows.length ? <EmptyState/> : (
        <div className="ranking-scroll">
          <table className="dashboard-data-table ranking-table">
            <thead><tr><th>{column}</th><th>{RANKING_METRICS.find(option => option.value === metric)?.label}</th></tr></thead>
            <tbody>
              {rows.map(({ item, value }, index) => (
                <tr key={`${item.parent ?? ""}|${item.key}`}>
                  <td title={sliceLabel(item, kind)}><span className="ranking-position">#{index + 1}</span>{sliceLabel(item, kind)}</td>
                  <td>{rankingText(value, metric)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

/**
 * Os "Rankings de Menções" do v1 — seis tabelas, cada uma com a sua
 * métrica (volume, CENA, engajamento, alcance) — mais o que a aba já tinha
 * no v2 (`children`: marcas e conteúdos com mais interações).
 */
export function RankingsTab({ data, showsCena, sources, children }: { data: SocialReportDashboard; showsCena: boolean; sources: SourceVisibility; children?: ReactNode }) {
  // Os rankings de uma fonte só somem com ela (2026-10-30); "Top
  // Ambiente" compara as fontes, então só aparece com as duas.
  return (
    <div className="dashboard-section-grid">
      {sources.press && <RankingCard title="Top Veículos" column="Veículo" items={data.press_channels} kind="raw" showsCena={showsCena}/>}
      {sources.network && <RankingCard title="Top Publicador" column="Perfil" items={data.authors} kind="author" showsCena={showsCena}/>}
      {sources.press && sources.network && <RankingCard title="Top Ambiente" column="Fonte" items={data.sources} showsCena={showsCena}/>}
      {sources.press && <RankingCard title="Top Tipo de Mídia" column="Tipo" items={data.media_types ?? []} kind="media" showsCena={showsCena} unavailable={data.media_types === null ? NOT_IN_THIS_PHOTO : undefined}/>}
      {sources.network && <RankingCard title="Top Tipo de Rede" column="Rede social" items={data.network_channels} kind="raw" showsCena={showsCena}/>}
      <RankingCard title="Top Subtemas" column="Subtema" items={data.themes} kind="raw" showsCena={showsCena}/>
      {children}
    </div>
  );
}
